"use client";

import React from "react";
import { Calendar, Users, FileText, Heart, Loader, ArrowRight } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  id: 1,
  title: "Define Your Needs",
  description: "Tell us about your home size, family members, and specific security concerns.",
  icon: Calendar
}, {
  id: 2,
  title: "Quick Questions",
  description: "Answer a few quick questions and we'll point you toward the right buying guide for your home.",
  icon: Users
}, {
  id: 3,
  title: "Expert Verification",
  description: "We cross-reference results with our latest hands-on testing and research data.",
  icon: FileText
}, {
  id: 4,
  title: "Get Your System",
  description: "Receive a personalized recommendation for the best security system for your home.",
  icon: Heart
}];
function EnrollmentForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 bg-secondary text-secondary-foreground rounded-lg flex items-center justify-center mb-4">
          <Heart className="w-8 h-8" />
        </div>
        <h3 className="text-2xl font-default font-bold text-foreground">Analysis Complete!</h3>
        <p className="text-muted-foreground max-w-sm">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 rounded-lg border-primary text-primary hover:bg-primary/5">
          Start New Search
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 gap-5">
        <FormField control={control} name="firstName" rules={{
        required: "First name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default font-semibold">First Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane" className="bg-background border-muted focus-visible:ring-secondary rounded-lg" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default font-semibold">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" className="bg-background border-muted focus-visible:ring-secondary rounded-lg" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full rounded-lg bg-secondary text-secondary-foreground hover:bg-secondary/90 h-12 text-base font-default font-bold shadow-md transition-all">
        {isSubmitting ? <>
            <Loader className="mr-2 h-5 w-5 animate-spin" />
            Analyzing Data...
          </> : <>
            Find My Perfect System
            <ArrowRight className="ml-2 h-5 w-5" />
          </>}
      </Button>
    </Form>;
}
export default function FindYourPerfectSystem() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="255050" id="find-your-perfect-system" ref={sectionRef} className="py-20 md:py-32 bg-background text-foreground relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-lg bg-secondary/5 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-lg bg-primary/5 blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-7xl">
        <div className={`text-center max-w-3xl mx-auto mb-16 md:mb-24 transition-all duration-700 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block px-4 py-1.5 mb-6 text-xs font-default font-bold tracking-widest text-destructive uppercase bg-destructive/5 rounded-lg border border-destructive/10">
            Expert Research
          </span>
          <h2 className="text-3xl md:text-5xl font-default font-bold text-primary mb-6 tracking-tight">
            Find Your Perfect System
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed font-default">Answer a few quick questions and we'll point you to the right buying guide for your home.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          
          <div className={`lg:col-span-5 transition-all duration-700 delay-200 transform ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <div className="relative">
              <div className="absolute left-[1.15rem] top-4 bottom-4 w-0.5 bg-border hidden sm:block" />
              
              <div className="space-y-10">
                {steps.map((step, i) => <div key={step.id} data-index={i} className="relative flex flex-col sm:flex-row gap-6 sm:gap-8 group">
                    <div className="flex-shrink-0 relative z-10">
                      <div className="w-10 h-10 rounded-lg bg-chart-5 text-primary-foreground flex items-center justify-center font-default font-bold text-lg shadow-md border-2 border-background group-hover:scale-110 transition-transform duration-300">
                        {step.id}
                      </div>
                    </div>
                    <div className="pt-1 sm:pt-1.5">
                      <h3 className="text-xl font-default font-bold text-primary mb-2 flex items-center gap-2">
                        {step.title}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed font-default">
                        {step.description}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>

          <div className={`lg:col-span-7 transition-all duration-700 delay-400 transform ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <Card className="border-primary shadow-xl bg-card backdrop-blur-sm rounded-2xl">
              <CardHeader className="pb-6 border-b border-border bg-card rounded-t-2xl">
                <CardTitle className="text-2xl font-default font-bold text-primary">Start Your Match</CardTitle>
                <CardDescription className="text-base font-default">Fill out the form below and we will generate your personalized security profile.</CardDescription>
              </CardHeader>
              <CardContent className="pt-8">
                <FormProvider formKey="find_system_form_home" formId="find_system_form_home" sectionName="Find Your Perfect System" defaultValues={{
                firstName: "",
                email: ""
              }} submitText="Find My Match" successMessage="Our AI has analyzed your data! Check your email for your personalized security system recommendation.">
                  <EnrollmentForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}